import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Palette, Delete, ChevronLeft, Shield, Plus, Trash2, Check, Scale,
  Search, Clock, Play, Pause, Beaker, Sparkles, Camera, Copy, X,
} from "lucide-react";
import { useBleScale } from "../lib/useBleScale";
import ScaleWidget from "../components/ScaleWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const KIOSK_CODE_STORAGE_KEY = "salonflow_kiosk_code"; // shared with the Time Clock kiosk

const OZ_TO_G = 28.3495;
const gramsOf = (amt, unit) => (unit === "oz" ? (Number(amt) || 0) * OZ_TO_G : (Number(amt) || 0));
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const newBowl = (n) => ({ bowl_id: `b${Date.now()}${n}`, label: `Bowl ${n}`, components: [], leftover: "", tare: "", rwUnit: "g" });

const TYPE_BADGE = {
  color: "bg-violet-500/20 text-violet-200 border-violet-400/30",
  developer: "bg-sky-500/20 text-sky-200 border-sky-400/30",
  lightener: "bg-amber-500/20 text-amber-200 border-amber-400/30",
  toner: "bg-rose-500/20 text-rose-200 border-rose-400/30",
  additive: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
};

export default function ColorBarKioskPage() {
  const [paired, setPaired] = useState(() => !!localStorage.getItem(KIOSK_CODE_STORAGE_KEY));
  const [pairingInput, setPairingInput] = useState("");
  const [pairingError, setPairingError] = useState("");
  const [pairingSubmitting, setPairingSubmitting] = useState(false);

  const [stage, setStage] = useState("grid"); // grid | pin | builder | confirm
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [savedPin, setSavedPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [products, setProducts] = useState([]);
  const [multiplier, setMultiplier] = useState(2.75);

  const [clientTab, setClientTab] = useState("today"); // today | search
  const [todayAppts, setTodayAppts] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [client, setClient] = useState(null); // {client_id, name, appointment_id?}
  const [history, setHistory] = useState([]);

  const [bowls, setBowls] = useState([newBowl(1)]);
  const [editingBowl, setEditingBowl] = useState(null); // index of bowl open in popup
  const [scaleOpen, setScaleOpen] = useState(false);
  const [developer, setDeveloper] = useState("");
  const [notes, setNotes] = useState("");
  const [timerSecs, setTimerSecs] = useState(0);
  const [timerOn, setTimerOn] = useState(false);
  const [result, setResult] = useState(null);
  const [photoShots, setPhotoShots] = useState([]); // [{label, url}]
  const [photoBusy, setPhotoBusy] = useState(false);

  const uploadPhoto = async (label, file) => {
    if (!file || !result) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", selected.user_id);
      fd.append("pin", savedPin);
      fd.append("label", label);
      fd.append("file", file);
      await axios.post(`${API}/color-bar/formulas/${result.formula_id}/photo`, fd,
        { headers: { "X-Kiosk-Code": kioskCode(), "Content-Type": "multipart/form-data" } });
      setPhotoShots((s) => [...s, { label, url: URL.createObjectURL(file) }]);
    } catch (e) {
      setError(e.response?.data?.detail || "Photo upload failed");
    }
    setPhotoBusy(false);
  };

  const kioskCode = () => localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "";
  const headers = { "X-Kiosk-Code": kioskCode() };
  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.product_id, p])), [products]);

  // ---- pairing ----
  const verifyPairing = async (code) => {
    setPairingSubmitting(true); setPairingError("");
    try {
      await axios.post(`${API}/kiosk/verify-code`, { code });
      localStorage.setItem(KIOSK_CODE_STORAGE_KEY, code);
      setPaired(true);
    } catch (err) {
      setPairingError(err.response?.data?.detail || "Invalid code. Ask an owner or manager for the 6-digit pairing code.");
    }
    setPairingSubmitting(false);
  };

  const loadStaffAndProducts = useCallback(async () => {
    if (!paired) return;
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/color-bar/staff`, { headers: { "X-Kiosk-Code": kioskCode() } }),
        axios.get(`${API}/color-bar/products`, { headers: { "X-Kiosk-Code": kioskCode() } }),
      ]);
      setStaff((s.data || []).filter((x) => x.has_pin));
      setProducts(p.data?.products || []);
      setMultiplier(p.data?.charge_multiplier || 2.75);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(KIOSK_CODE_STORAGE_KEY); setPaired(false); }
    }
  }, [paired]);

  useEffect(() => { loadStaffAndProducts(); }, [loadStaffAndProducts]);

  // ---- processing timer ----
  useEffect(() => {
    if (!timerOn) return;
    const id = setInterval(() => setTimerSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [timerOn]);
  const timerMins = Math.round(timerSecs / 60);
  const mmss = `${String(Math.floor(timerSecs / 60)).padStart(2, "0")}:${String(timerSecs % 60).padStart(2, "0")}`;

  // ---- costs ----
  const compCost = (c) => gramsOf(c.amount, c.unit) * (productMap[c.product_id]?.cost_per_gram || 0);
  const bowlCost = (b) => b.components.reduce((s, c) => s + compCost(c), 0);
  const totalCost = bowls.reduce((s, b) => s + bowlCost(b), 0);
  const suggested = totalCost * multiplier;
  const hasAnyComponent = bowls.some((b) => b.components.length > 0);

  const bowlWaste = (b) => {
    const mixed = b.components.reduce((s, c) => s + gramsOf(c.amount, c.unit), 0);
    const netG = Math.max(0, gramsOf(b.leftover, b.rwUnit) - gramsOf(b.tare, b.rwUnit));
    const frac = mixed > 0 ? Math.min(netG, mixed) / mixed : 0;
    return bowlCost(b) * frac;
  };
  const bowlSummary = (b) => b.components.filter((c) => c.product_id).map((c) => {
    const p = productMap[c.product_id];
    return `${p?.name || "Product"} ${c.amount || 0}${c.unit}`;
  }).join("  +  ");

  const reset = () => {
    setStage("grid"); setSelected(null); setPin(""); setSavedPin(""); setError("");
    setClient(null); setClientTab("today"); setTodayAppts([]); setSearchQ(""); setSearchResults([]);
    setHistory([]); setBowls([newBowl(1)]); setEditingBowl(null); setScaleOpen(false);
    setDeveloper(""); setNotes(""); setTimerSecs(0); setTimerOn(false); setResult(null); setPhotoShots([]);
    loadStaffAndProducts();
  };

  const selectStaff = (s) => { setSelected(s); setPin(""); setError(""); setStage("pin"); };

  const pressKey = (d) => {
    setError("");
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) enterBuilder(next);
  };
  const backspace = () => { setError(""); setPin((p) => p.slice(0, -1)); };

  const enterBuilder = async (fullPin) => {
    setSubmitting(true); setError("");
    try {
      const res = await axios.post(`${API}/color-bar/today-appointments`,
        { user_id: selected.user_id, pin: fullPin }, { headers: { "X-Kiosk-Code": kioskCode() } });
      setSavedPin(fullPin);
      setTodayAppts(res.data || []);
      setClientTab((res.data || []).length ? "today" : "search");
      setStage("builder");
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(KIOSK_CODE_STORAGE_KEY); setPaired(false); }
      else { setError(err.response?.data?.detail || "Incorrect PIN"); setPin(""); }
    }
    setSubmitting(false);
  };

  const doSearch = async () => {
    try {
      const res = await axios.post(`${API}/color-bar/search-clients`,
        { user_id: selected.user_id, pin: savedPin, q: searchQ }, { headers: { "X-Kiosk-Code": kioskCode() } });
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
  };

  const chooseClient = async (c) => {
    setClient(c);
    try {
      const res = await axios.post(`${API}/color-bar/client-history`,
        { user_id: selected.user_id, pin: savedPin, client_id: c.client_id }, { headers: { "X-Kiosk-Code": kioskCode() } });
      setHistory(res.data || []);
    } catch { setHistory([]); }
  };

  const duplicateLast = () => {
    const last = history[0];
    if (!last) return;
    setBowls((last.bowls || []).map((b, i) => ({
      bowl_id: `b${Date.now()}${i}`, label: b.label || `Bowl ${i + 1}`,
      components: (b.components || []).map((c) => ({ product_id: c.product_id || "", amount: String(c.amount ?? ""), unit: c.unit || "g" })),
      leftover: "", tare: "", rwUnit: "g",
    })));
    setDeveloper(last.developer || "");
    setNotes(last.notes || "");
  };

  // ---- bowl editing ----
  const addBowl = () => { setBowls((b) => [...b, newBowl(b.length + 1)]); setEditingBowl(bowls.length); };
  const removeBowl = (i) => setBowls((b) => b.filter((_, idx) => idx !== i));
  const addComponent = (i) => setBowls((b) => b.map((bw, idx) => idx === i
    ? { ...bw, components: [...bw.components, { product_id: "", amount: "", unit: "g" }] } : bw));
  const updateComponent = (bi, ci, patch) => setBowls((b) => b.map((bw, idx) => idx === bi
    ? { ...bw, components: bw.components.map((c, cx) => cx === ci ? { ...c, ...patch } : c) } : bw));
  const removeComponent = (bi, ci) => setBowls((b) => b.map((bw, idx) => idx === bi
    ? { ...bw, components: bw.components.filter((_, cx) => cx !== ci) } : bw));

  // Live Bluetooth smart-scale feed (Chrome stations).
  const scale = useBleScale();
  const captureComp = (bi, ci) => {
    if (!scale.connected) { setScaleOpen(true); setError("Connect the Bluetooth scale first."); return; }
    setError("");
    updateComponent(bi, ci, { amount: String(scale.weight), unit: "g" });
  };

  const saveFormula = async () => {
    if (!client) { setError("Pick a client first"); return; }
    if (!hasAnyComponent) { setError("Add at least one product"); return; }
    setSubmitting(true); setError("");
    try {
      const payload = {
        user_id: selected.user_id, pin: savedPin,
        client_id: client.client_id, client_name: client.name,
        appointment_id: client.appointment_id || null,
        developer, processing_minutes: timerMins || null, notes,
        bowls: bowls.filter((b) => b.components.length).map((b) => ({
          bowl_id: b.bowl_id, label: b.label,
          components: b.components.filter((c) => c.product_id).map((c) => ({
            product_id: c.product_id, amount: Number(c.amount) || 0, unit: c.unit,
          })),
          waste_amount: Number(b.leftover) || 0, waste_unit: b.rwUnit || "g", tare: Number(b.tare) || 0,
        })),
      };
      const res = await axios.post(`${API}/color-bar/formulas`, payload, { headers: { "X-Kiosk-Code": kioskCode() } });
      setResult(res.data);
      setStage("confirm");
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(KIOSK_CODE_STORAGE_KEY); setPaired(false); }
      else setError(err.response?.data?.detail || "Couldn't save formula");
    }
    setSubmitting(false);
  };

  // auto-return after confirm (long enough to attach photos first)
  useEffect(() => {
    if (stage !== "confirm") return;
    const id = setTimeout(reset, 45000);
    return () => clearTimeout(id);
  }, [stage]); // eslint-disable-line

  // ================= PAIRING GATE =================
  if (!paired) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#120a24] to-[#0b1026] flex items-center justify-center" data-testid="colorbar-pair-screen">
        <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-10 max-w-md w-full mx-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-sky-500 flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white mb-2">Pair this device</h1>
            <p className="text-white/50 font-['Manrope'] mb-8 leading-relaxed">
              Enter the 6-digit pairing code from Time Clock → Kiosk Pairing. (Same code as your clock-in kiosk.)
            </p>
            <input type="tel" inputMode="numeric" autoFocus value={pairingInput}
              onChange={(e) => { setPairingError(""); setPairingInput(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
              placeholder="000000" data-testid="colorbar-pair-input"
              className="w-full h-16 text-center bg-white/10 border border-white/10 rounded-2xl text-white text-3xl tracking-[0.4em] font-mono font-semibold placeholder-white/20 focus:outline-none focus:border-fuchsia-400/40" />
            {pairingError && <p className="text-rose-400 text-sm mt-3 font-['Manrope']" data-testid="colorbar-pair-error">{pairingError}</p>}
            <button onClick={() => verifyPairing(pairingInput)} disabled={pairingInput.length !== 6 || pairingSubmitting}
              data-testid="colorbar-pair-submit"
              className="w-full mt-6 h-12 rounded-full bg-white text-[#120a24] font-semibold font-['Outfit'] hover:bg-white/90 transition-colors disabled:opacity-30">
              {pairingSubmitting ? "Verifying…" : "Pair Device"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#1a0b2e] via-[#120a24] to-[#0b1026] overflow-x-hidden relative" data-testid="colorbar-kiosk-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-fuchsia-500/10 blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-sky-500 flex items-center justify-center">
            <Palette className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="text-white/90 text-sm font-semibold font-['Outfit']">Color Bar</p>
            <p className="text-white/40 text-[11px] font-['Manrope'] uppercase tracking-[0.18em] font-semibold">Formula Tracking</p>
          </div>
        </div>
        {stage !== "grid" && stage !== "confirm" && (
          <button onClick={reset} data-testid="colorbar-exit"
            className="text-white/50 hover:text-white text-sm font-['Manrope'] flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Start over
          </button>
        )}
      </div>

      <div className="relative max-w-6xl mx-auto px-8 pb-12">

        {/* STAGE: GRID */}
        {stage === "grid" && (
          <div data-testid="colorbar-grid">
            <div className="mb-8 text-center">
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-2">Tap your name to log a formula.</h1>
              <p className="text-white/50 font-['Manrope'] text-lg">Confirm with your 4-digit PIN.</p>
            </div>
            {staff.length === 0 ? (
              <p className="text-center text-white/50 font-['Manrope'] mt-16">No staff with PINs yet. Ask a manager to set PINs from Staff → Set PIN.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {staff.map((s) => (
                  <button key={s.user_id} onClick={() => selectStaff(s)} data-testid={`colorbar-staff-${s.user_id}`}
                    className="group relative aspect-[3/4] rounded-3xl flex flex-col items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/25 backdrop-blur transition-all">
                    <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold font-['Outfit'] shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${s.color || "#d946ef"}, ${s.color || "#d946ef"}aa)` }}>
                      {s.name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                    <p className="mt-5 text-white font-['Outfit'] font-semibold text-lg">{s.name}</p>
                    <p className="text-white/40 text-xs font-['Manrope'] capitalize">{(s.title || s.role || "").replace(/_/g, " ")}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STAGE: PIN */}
        {stage === "pin" && selected && (
          <div className="max-w-md mx-auto mt-6" data-testid="colorbar-pin">
            <button onClick={reset} className="flex items-center gap-1 text-white/60 hover:text-white mb-6 text-sm font-['Manrope']"><ChevronLeft className="w-4 h-4" />Back</button>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-8">
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold font-['Outfit']"
                  style={{ background: `linear-gradient(135deg, ${selected.color || "#d946ef"}, ${selected.color || "#d946ef"}aa)` }}>
                  {selected.name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <p className="mt-5 text-white font-['Outfit'] text-2xl font-semibold">Hi {selected.name.split(" ")[0]}</p>
                <p className="text-white/50 text-sm mt-1 font-['Manrope']">Enter your 4-digit PIN</p>
              </div>
              <div className="flex items-center justify-center gap-3 mt-8 mb-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "bg-white" : "bg-white/15"}`} />
                ))}
              </div>
              {error && <p className="text-rose-400 text-sm text-center mt-3 font-['Manrope']" data-testid="colorbar-pin-error">{error}</p>}
              <div className="grid grid-cols-3 gap-3 mt-8">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} onClick={() => pressKey(String(n))} disabled={submitting} data-testid={`colorbar-key-${n}`}
                    className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 text-white text-2xl font-semibold font-['Outfit'] disabled:opacity-50">{n}</button>
                ))}
                <div />
                <button onClick={() => pressKey("0")} disabled={submitting} data-testid="colorbar-key-0"
                  className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 text-white text-2xl font-semibold font-['Outfit'] disabled:opacity-50">0</button>
                <button onClick={backspace} disabled={submitting || pin.length === 0} data-testid="colorbar-key-del"
                  className="aspect-square rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 text-white flex items-center justify-center disabled:opacity-30"><Delete className="w-6 h-6" /></button>
              </div>
            </div>
          </div>
        )}

        {/* STAGE: BUILDER */}
        {stage === "builder" && (
          <div data-testid="colorbar-builder" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT: client + bowls */}
            <div className="lg:col-span-2 space-y-5">
              {/* Client */}
              {client ? (
                <div className="bg-white/5 border border-white/10 rounded-3xl px-5 py-4 backdrop-blur flex items-center justify-between gap-3" data-testid="colorbar-selected-client">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-0.5">Client</p>
                    <p className="text-white font-['Outfit'] text-xl font-semibold truncate">{client.name}</p>
                    {history.length > 0 && <p className="text-white/40 text-xs font-['Manrope']">{history.length} past formula{history.length !== 1 ? "s" : ""} on file</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {history.length > 0 && (
                      <button onClick={duplicateLast} data-testid="colorbar-duplicate-last"
                        className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-200 text-xs font-semibold font-['Manrope'] hover:bg-fuchsia-500/20">
                        <Copy className="w-3.5 h-3.5" /> Use last
                      </button>
                    )}
                    <button onClick={() => { setClient(null); setHistory([]); }} data-testid="colorbar-change-client"
                      className="text-white/50 hover:text-white text-sm font-['Manrope'] underline">Change</button>
                  </div>
                </div>
              ) : (
                <div className="bg-white/5 border border-white/10 rounded-3xl p-5 backdrop-blur">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-3">Client</p>
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setClientTab("today")} data-testid="colorbar-tab-today"
                      className={`px-4 h-9 rounded-full text-sm font-semibold font-['Manrope'] ${clientTab === "today" ? "bg-white text-[#120a24]" : "bg-white/5 text-white/60 border border-white/10"}`}>Today's color clients</button>
                    <button onClick={() => setClientTab("search")} data-testid="colorbar-tab-search"
                      className={`px-4 h-9 rounded-full text-sm font-semibold font-['Manrope'] ${clientTab === "search" ? "bg-white text-[#120a24]" : "bg-white/5 text-white/60 border border-white/10"}`}>Search</button>
                  </div>
                  {clientTab === "today" ? (
                    todayAppts.length === 0 ? (
                      <p className="text-white/40 text-sm font-['Manrope']">No color appointments today — use Search.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                        {todayAppts.map((a) => (
                          <button key={a.appointment_id} data-testid={`colorbar-appt-${a.appointment_id}`}
                            onClick={() => chooseClient({ client_id: a.client_id, name: a.client_name, appointment_id: a.appointment_id })}
                            className="text-left p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10">
                            <p className="text-white font-['Outfit'] font-semibold">{a.client_name}</p>
                            <p className="text-white/40 text-xs font-['Manrope']">{(a.service_names || []).join(", ")}</p>
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-white/10 rounded-xl px-3">
                          <Search className="w-4 h-4 text-white/40" />
                          <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
                            placeholder="Name, phone or email" data-testid="colorbar-search-input"
                            className="flex-1 h-11 bg-transparent text-white placeholder-white/30 font-['Manrope'] focus:outline-none" />
                        </div>
                        <button onClick={doSearch} data-testid="colorbar-search-btn" className="px-4 h-11 rounded-xl bg-white text-[#120a24] font-semibold font-['Manrope']">Go</button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 max-h-56 overflow-y-auto">
                        {searchResults.map((c) => (
                          <button key={c.client_id} data-testid={`colorbar-client-${c.client_id}`}
                            onClick={() => chooseClient({ client_id: c.client_id, name: c.name })}
                            className="text-left p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10">
                            <p className="text-white font-['Outfit'] font-semibold">{c.name}</p>
                            {c.phone && <p className="text-white/40 text-xs font-['Manrope']">{c.phone}</p>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Past formulas */}
              {client && history.length > 0 && (
                <details className="bg-white/5 border border-white/10 rounded-3xl p-5 backdrop-blur" data-testid="colorbar-history">
                  <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Past formulas ({history.length})</summary>
                  <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
                    {history.map((f) => (
                      <div key={f.formula_id} className="p-3 rounded-2xl bg-white/5 border border-white/10">
                        <div className="flex justify-between text-white/80 text-sm font-['Manrope']">
                          <span>{f.service_date} · {f.stylist_name}</span>
                          <span>{money(f.total_cost)}</span>
                        </div>
                        {(f.bowls || []).map((b) => (
                          <p key={b.bowl_id} className="text-white/50 text-xs font-['Manrope'] mt-1">
                            <span className="text-white/70">{b.label}:</span> {(b.components || []).map((c) => `${c.product_name} ${c.amount}${c.unit}`).join(" + ")}
                          </p>
                        ))}
                        {f.notes && <p className="text-white/40 text-xs italic font-['Manrope'] mt-1">"{f.notes}"</p>}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Scale (collapsible) */}
              <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur overflow-hidden">
                <button onClick={() => setScaleOpen((o) => !o)} data-testid="colorbar-scale-toggle"
                  className="w-full flex items-center justify-between px-5 py-3.5">
                  <span className="flex items-center gap-2 text-white/80 text-sm font-semibold font-['Manrope']">
                    <Scale className="w-4 h-4" /> Bluetooth scale
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${scale.connected ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white/40"}`}>
                      {scale.connected ? "Connected" : "Off"}
                    </span>
                  </span>
                  <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform ${scaleOpen ? "-rotate-90" : "rotate-0"}`} />
                </button>
                {scaleOpen && <div className="px-5 pb-5"><ScaleWidget scale={scale} variant="dark" testid="colorbar-scale" /></div>}
              </div>

              {/* Bowls */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-3">Bowls</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {bowls.map((b, bi) => {
                    const count = b.components.filter((c) => c.product_id).length;
                    return (
                      <div key={b.bowl_id} data-testid={`colorbar-bowl-${bi}`}
                        className="bg-white/5 border border-white/10 rounded-3xl p-4 backdrop-blur hover:border-white/25 transition-colors flex flex-col">
                        <button onClick={() => setEditingBowl(bi)} data-testid={`colorbar-edit-bowl-${bi}`} className="w-full text-left flex-1">
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <span className="flex items-center gap-2 text-white font-['Outfit'] font-semibold truncate">
                              <Beaker className="w-4 h-4 text-fuchsia-300 shrink-0" />{b.label}
                            </span>
                            <span className="text-white/70 text-sm font-['Manrope'] tabular-nums shrink-0">{money(bowlCost(b))}</span>
                          </div>
                          {count ? (
                            <p className="text-white/50 text-xs font-['Manrope'] leading-relaxed line-clamp-2">{bowlSummary(b)}</p>
                          ) : (
                            <p className="text-white/30 text-xs font-['Manrope'] italic">Tap to add products…</p>
                          )}
                          {b.leftover !== "" && <p className="text-amber-200/80 text-[11px] font-['Manrope'] mt-1 tabular-nums">{money(bowlWaste(b))} waste</p>}
                        </button>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                          <button onClick={() => setEditingBowl(bi)}
                            className="text-fuchsia-200/80 hover:text-fuchsia-200 text-xs font-semibold font-['Manrope']">
                            {count ? "Edit bowl" : "Add products"}
                          </button>
                          {bowls.length > 1 && (
                            <button onClick={() => removeBowl(bi)} data-testid={`colorbar-remove-bowl-${bi}`} className="text-white/40 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={addBowl} data-testid="colorbar-add-bowl"
                    className="rounded-3xl border border-dashed border-white/20 text-white/50 hover:text-white hover:border-white/40 text-sm font-['Manrope'] font-semibold flex flex-col items-center justify-center gap-2 p-6 min-h-[132px]">
                    <Plus className="w-6 h-6" /> Add bowl
                  </button>
                </div>
              </div>

              {/* Developer + timer + notes */}
              <div className="bg-white/5 border border-white/10 rounded-3xl p-5 backdrop-blur grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-2">Developer</p>
                  <input value={developer} onChange={(e) => setDeveloper(e.target.value)} placeholder="e.g. 20 vol" data-testid="colorbar-developer"
                    className="w-full h-11 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-['Manrope'] px-3 focus:outline-none" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-2">Processing timer</p>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-['Outfit'] text-xl font-semibold tabular-nums w-20" data-testid="colorbar-timer">{mmss}</span>
                    <button onClick={() => setTimerOn((v) => !v)} data-testid="colorbar-timer-toggle"
                      className="px-3 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-['Manrope'] flex items-center gap-1.5">
                      {timerOn ? <><Pause className="w-4 h-4" />Pause</> : <><Play className="w-4 h-4" />Start</>}
                    </button>
                    <span className="text-white/40 text-xs font-['Manrope']">{timerMins} min</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-2">Notes</p>
                  <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 2000))} placeholder="Root touch-up, pulled through last 10 min…"
                    data-testid="colorbar-notes"
                    className="w-full rounded-xl bg-white/10 border border-white/10 text-white text-sm font-['Manrope'] px-3 py-2.5 resize-none focus:outline-none" />
                </div>
              </div>
            </div>

            {/* RIGHT: cost summary + save */}
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur sticky top-6" data-testid="colorbar-summary">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-4">Cost summary</p>
                <div className="space-y-2">
                  {bowls.filter((b) => b.components.length).map((b, i) => (
                    <div key={i} className="flex justify-between text-sm font-['Manrope']">
                      <span className="text-white/60">{b.label}</span>
                      <span className="text-white/80 tabular-nums">{money(bowlCost(b))}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 my-4" />
                <div className="flex justify-between items-baseline">
                  <span className="text-white/60 text-sm font-['Manrope']">Product cost</span>
                  <span className="text-white font-['Outfit'] text-2xl font-semibold tabular-nums" data-testid="colorbar-total-cost">{money(totalCost)}</span>
                </div>
                <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500/20 to-sky-500/20 border border-white/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-['Manrope']">Suggested charge</span>
                  </div>
                  <p className="text-white font-['Outfit'] text-3xl font-semibold tabular-nums" data-testid="colorbar-suggested">{money(suggested)}</p>
                  <p className="text-white/40 text-xs font-['Manrope'] mt-1">{multiplier}× product cost</p>
                </div>
                {error && <p className="text-rose-400 text-sm mt-4 font-['Manrope']" data-testid="colorbar-save-error">{error}</p>}
                <button onClick={saveFormula} disabled={submitting || !client || !hasAnyComponent} data-testid="colorbar-save"
                  className="w-full mt-5 h-14 rounded-2xl bg-white text-[#120a24] font-['Outfit'] font-semibold text-lg hover:bg-white/90 transition-colors disabled:opacity-30 flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />{submitting ? "Saving…" : "Save formula"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STAGE: CONFIRM */}
        {stage === "confirm" && result && (
          <div className="max-w-lg mx-auto mt-10" data-testid="colorbar-confirm">
            <div className="rounded-[32px] p-10 text-center bg-gradient-to-br from-fuchsia-500/20 to-sky-500/20 border border-fuchsia-400/30">
              <div className="w-20 h-20 mx-auto rounded-full bg-fuchsia-500 shadow-2xl flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="font-['Outfit'] text-4xl font-semibold text-white tracking-tight mb-2">Formula saved!</h2>
              <p className="text-white/70 font-['Manrope'] text-lg mb-6">{result.client_name} · {result.stylist_name}</p>
              <div className="inline-flex gap-8 bg-white/10 rounded-2xl px-8 py-5">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 font-['Manrope']">Product cost</p>
                  <p className="text-white font-['Outfit'] text-2xl font-semibold tabular-nums">{money(result.total_cost)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 font-['Manrope']">Suggested charge</p>
                  <p className="text-white font-['Outfit'] text-2xl font-semibold tabular-nums">{money(result.suggested_charge)}</p>
                </div>
              </div>

              {/* Before / after photos */}
              <div className="mt-6" data-testid="colorbar-photos">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 font-['Manrope'] mb-3">Add before / after photos</p>
                {photoShots.length > 0 && (
                  <div className="flex justify-center gap-3 mb-3">
                    {photoShots.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={p.url} alt={p.label} className="w-20 h-20 rounded-xl object-cover border border-white/20" />
                        <span className="absolute bottom-0 inset-x-0 text-[9px] text-center text-white bg-black/50 rounded-b-xl capitalize py-0.5">{p.label}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-center gap-3">
                  {["before", "after"].map((lbl) => (
                    <label key={lbl} data-testid={`colorbar-photo-${lbl}`}
                      className={`cursor-pointer inline-flex items-center gap-2 px-4 h-11 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-['Manrope'] ${photoBusy ? "opacity-50 pointer-events-none" : ""}`}>
                      <Camera className="w-4 h-4" /> <span className="capitalize">{lbl}</span>
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadPhoto(lbl, f); }} />
                    </label>
                  ))}
                </div>
                {error && <p className="text-rose-300 text-xs mt-3 font-['Manrope']">{error}</p>}
              </div>

              <p className="text-white/30 text-xs font-['Manrope'] mt-8">Returning to the roster…</p>
              <button onClick={reset} data-testid="colorbar-done" className="block mx-auto mt-4 text-white/60 hover:text-white text-sm font-['Manrope'] underline">Log another</button>
            </div>
          </div>
        )}
      </div>

      {/* ============ BOWL EDITOR POPUP ============ */}
      {editingBowl !== null && bowls[editingBowl] && (() => {
        const bi = editingBowl;
        const b = bowls[bi];
        const setBowl = (patch) => setBowls((arr) => arr.map((x, i) => i === bi ? { ...x, ...patch } : x));
        const close = () => setEditingBowl(null);
        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="colorbar-bowl-modal">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
            <div className="relative w-full sm:max-w-xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#241136] to-[#150c28] border border-white/15 rounded-t-[32px] sm:rounded-[32px] p-6 shadow-2xl">
              {/* header */}
              <div className="flex items-center justify-between mb-5 gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Beaker className="w-5 h-5 text-fuchsia-300 shrink-0" />
                  <input value={b.label} onChange={(e) => setBowl({ label: e.target.value })} data-testid={`colorbar-bowl-label-${bi}`}
                    className="bg-transparent text-white font-['Outfit'] font-semibold text-xl focus:outline-none border-b border-transparent focus:border-white/20 w-full" />
                </div>
                <span className="text-white/60 text-sm font-['Manrope'] tabular-nums">{money(bowlCost(b))}</span>
                <button onClick={close} data-testid="colorbar-bowl-modal-close" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {/* scale status hint */}
              <div className="flex items-center justify-between mb-4 px-3 py-2 rounded-xl bg-white/5 border border-white/10">
                <span className="flex items-center gap-2 text-xs font-['Manrope'] text-white/60">
                  <Scale className="w-3.5 h-3.5" />
                  Scale {scale.connected ? <span className="text-emerald-300">connected · {scale.weight}g</span> : <span className="text-white/40">not connected</span>}
                </span>
                {!scale.connected && (
                  <button onClick={() => { close(); setScaleOpen(true); }} className="text-sky-300 hover:text-sky-200 text-xs font-semibold font-['Manrope']">Connect</button>
                )}
              </div>

              {/* components */}
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope'] mb-2">Products</p>
              <div className="space-y-2">
                {b.components.map((c, ci) => (
                  <div key={ci} className="flex items-center gap-2" data-testid={`colorbar-comp-${bi}-${ci}`}>
                    <select value={c.product_id} onChange={(e) => updateComponent(bi, ci, { product_id: e.target.value })}
                      data-testid={`colorbar-comp-product-${bi}-${ci}`}
                      className="flex-1 min-w-0 h-11 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-['Manrope'] px-3 focus:outline-none [color-scheme:dark]">
                      <option value="">Select product…</option>
                      {products.map((p) => (
                        <option key={p.product_id} value={p.product_id}>{p.brand ? `${p.brand} ` : ""}{p.name}{p.shade ? ` (${p.shade})` : ""} · {p.type}</option>
                      ))}
                    </select>
                    <input type="number" min="0" value={c.amount} onChange={(e) => updateComponent(bi, ci, { amount: e.target.value })}
                      placeholder="0" data-testid={`colorbar-comp-amount-${bi}-${ci}`}
                      className="w-16 h-11 rounded-xl bg-white/10 border border-white/10 text-white text-sm text-center font-['Manrope'] focus:outline-none" />
                    <div className="flex rounded-xl overflow-hidden border border-white/10">
                      {["g", "oz"].map((u) => (
                        <button key={u} onClick={() => updateComponent(bi, ci, { unit: u })} data-testid={`colorbar-comp-unit-${bi}-${ci}-${u}`}
                          className={`w-8 h-11 text-xs font-semibold font-['Manrope'] ${c.unit === u ? "bg-white text-[#120a24]" : "bg-white/5 text-white/50"}`}>{u}</button>
                      ))}
                    </div>
                    <button onClick={() => captureComp(bi, ci)} title={scale.connected ? "Weigh with scale" : "Connect scale"} data-testid={`colorbar-weigh-${bi}-${ci}`}
                      className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 transition-colors ${scale.connected ? "bg-sky-500/20 border-sky-400/40 text-sky-200 hover:bg-sky-500/30" : "bg-white/5 border-white/10 text-white/40 hover:text-white"}`}><Scale className="w-4 h-4" /></button>
                    <button onClick={() => removeComponent(bi, ci)} data-testid={`colorbar-remove-comp-${bi}-${ci}`} className="text-white/30 hover:text-rose-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => addComponent(bi)} data-testid={`colorbar-add-comp-${bi}`}
                  className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-['Manrope'] mt-1"><Plus className="w-4 h-4" /> Add product</button>
              </div>

              {/* reweigh */}
              {(() => {
                const wCost = bowlWaste(b);
                return (
                  <div className="mt-5 pt-5 border-t border-white/10" data-testid={`colorbar-reweigh-${bi}`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 font-['Manrope'] mb-2">Reweigh waste (before closing)</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input type="number" min="0" value={b.tare} onChange={(e) => setBowl({ tare: e.target.value })} placeholder="bowl tare" title="Empty bowl weight"
                        data-testid={`colorbar-tare-${bi}`} className="w-24 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-sm text-center font-['Manrope'] focus:outline-none" />
                      <input type="number" min="0" value={b.leftover} onChange={(e) => setBowl({ leftover: e.target.value })} placeholder="leftover" title="Scale reading of leftover"
                        data-testid={`colorbar-leftover-${bi}`} className="w-24 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-sm text-center font-['Manrope'] focus:outline-none" />
                      <div className="flex rounded-xl overflow-hidden border border-white/10">
                        {["g", "oz"].map((u) => <button key={u} onClick={() => setBowl({ rwUnit: u })} className={`w-9 h-10 text-xs font-semibold ${b.rwUnit === u ? "bg-amber-400 text-[#120a24]" : "bg-white/5 text-white/50"}`}>{u}</button>)}
                      </div>
                      {scale.connected && (
                        <>
                          <button onClick={() => setBowl({ tare: String(scale.weight), rwUnit: "g" })} data-testid={`colorbar-capture-tare-${bi}`}
                            className="h-10 px-3 rounded-xl bg-amber-400/90 text-[#120a24] text-xs font-semibold font-['Manrope'] hover:bg-amber-400 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Tare</button>
                          <button onClick={() => setBowl({ leftover: String(scale.weight), rwUnit: "g" })} data-testid={`colorbar-capture-leftover-${bi}`}
                            className="h-10 px-3 rounded-xl bg-sky-500/80 text-white text-xs font-semibold font-['Manrope'] hover:bg-sky-500 flex items-center gap-1"><Scale className="w-3.5 h-3.5" />Reweigh</button>
                        </>
                      )}
                      {b.leftover !== "" && <span className="text-xs text-amber-200 font-['Manrope'] tabular-nums">{money(wCost)} waste</span>}
                    </div>
                  </div>
                );
              })()}

              {/* footer */}
              <div className="flex items-center gap-3 mt-6">
                {bowls.length > 1 && (
                  <button onClick={() => { removeBowl(bi); close(); }} data-testid={`colorbar-modal-remove-bowl-${bi}`}
                    className="h-12 px-4 rounded-2xl border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm font-semibold font-['Manrope'] hover:bg-rose-500/20 flex items-center gap-1.5">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
                <button onClick={close} data-testid="colorbar-bowl-modal-done"
                  className="flex-1 h-12 rounded-2xl bg-white text-[#120a24] font-['Outfit'] font-semibold hover:bg-white/90 flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" /> Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
